/**
 * TeamWiseCurrentMonthProjectionCard.jsx
 *
 * Monthly version of the Team-Wise Projection chart.
 * Shows current month projections (Low/Mid/High) broken down by team.
 *
 * Data Source: Time Series projection of Closed Won (Monthly)
 * Redirect Dashboard: "Time Series projection of Closed Won (Monthly)"
 *
 * This is a thin wrapper component that:
 * 1. Uses the useMonthlyTeamProjectionData hook for data fetching
 * 2. Renders using the shared D3Linearity chart component (with xField="team")
 * 3. Wraps in RccCard with monthly-specific configuration
 *
 * Note: This card is only enabled for customers with monthly Time Series enabled (e.g., C0017).
 * The visibility is controlled via the INT_RCC_Cards_To_UI_Role_ID_Mapping database table.
 */

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import D3Linearity from "./D3Linearity";
import RccCard from "../RccCard";
import { useMonthlyTeamProjectionData } from "../hooks/useTeamProjectionData";

/**
 * Team-Wise Current Month Projection Card Component
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Card title (default: "Team-Wise Current Month Projection")
 * @param {string} props.redirectToDashboard - Dashboard to redirect on zoom
 *   (default: "Time Series projection of Closed Won (Monthly)")
 * @returns {JSX.Element} The monthly team projection card
 */
function TeamWiseCurrentMonthProjectionCard({ title, redirectToDashboard }) {
  const user = useSelector((state) => state.app?.user);

  // Use the monthly team data hook - all fetching/transformation logic is encapsulated
  const { teamData, appliedFilters, isLoading, error } =
    useMonthlyTeamProjectionData();

  const filteredTeamData = useMemo(() => {
    return (teamData || []).filter((row) => {
      const teamName = String(row?.team || "")
        .trim()
        .toLowerCase();
      return teamName !== "other";
    });
  }, [teamData]);

  const handleZoom = () => {
    console.log("Zooming Monthly Team Projection Chart...");
  };

  return (
    <RccCard
      title={title || "Team-Wise Current Month Projection"}
      isLoading={isLoading}
      onZoomClick={handleZoom}
      redirectToDashboard={
        redirectToDashboard || "Time Series projection of Closed Won (Monthly)"
      }
      appliedFilters={appliedFilters}
      focusVariant="current"
    >
      {/* Wrapper to ensure D3 Chart has relative positioning context */}
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {error ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "#999",
            }}
          >
            {error}
          </div>
        ) : filteredTeamData.length > 0 ? (
          // D3Linearity uses xField="team" to display team names on x-axis
          <D3Linearity
            data={filteredTeamData}
            xField="team"
            currencySymbol={user?.Currency || ""}
            periodType="month"
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "#999",
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default TeamWiseCurrentMonthProjectionCard;
